import { useEffect, useState } from "react";
import { Loader2, MapPin, Search } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";

export const CompanyFinder = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults(null);
      return;
    }
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/companies", { params: { q: term } });
        setResults(data.companies);
        setError("");
      } catch (e) {
        setError(errMsg(e));
      } finally {
        setBusy(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div data-testid="company-finder" className="rounded-xl border border-ink-600 bg-ink-800/60 p-5">
      <p className="font-mono text-[11px] uppercase tracking-widest text-gold">
        Looking for a specific company?
      </p>
      <p className="mt-2 text-sm text-slate-400">
        Search the operator you found online and book straight from their own page — no account needed.
      </p>
      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid="company-finder-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Company name or city"
          className="h-11 border-ink-600 bg-ink-900 pl-9 text-slate-100 placeholder:text-slate-600 focus-visible:ring-gold"
        />
        {busy && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-500" />}
      </div>

      {error && <p data-testid="company-finder-error" className="mt-3 text-sm text-red-400">{error}</p>}

      {results?.length === 0 && !busy && (
        <p data-testid="company-finder-empty" className="mt-4 text-sm text-slate-500">
          No match yet. Ask the company for their booking link — every operator here has one.
        </p>
      )}

      {results?.length > 0 && (
        <div className="mt-4 space-y-2">
          {results.map((c) => (
            <a
              key={c.handle}
              href={`/ride/${c.handle}`}
              data-testid={`company-finder-result-${c.handle}`}
              className="flex items-center gap-3 rounded-lg border border-ink-600 bg-ink-900/70 p-3 transition-colors hover:border-gold/50"
            >
              {c.logo_url?.startsWith("http") ? (
                <img src={c.logo_url} alt="" className="h-9 w-9 rounded bg-white/95 object-contain p-0.5" />
              ) : (
                <span className="flex h-9 w-9 items-center justify-center rounded bg-ink-800 font-display text-sm text-gold">
                  {c.name.slice(0, 1)}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-slate-100">{c.name}</span>
                {c.address && (
                  <span className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-slate-500">
                    <MapPin className="h-3 w-3 shrink-0" /> {c.address}
                  </span>
                )}
              </span>
              <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-gold">Book →</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
